import {
  GlobalStyle,
  ToastProvider,
  mixedTheme,
  BurgerMenuProvider,
  AuthSSOProvider,
} from '@vallourec-ui/vallourec-ui'

import React, { ReactNode, useContext, useEffect, useState } from 'react'
import { ThemeProvider } from 'styled-components'

import { useTranslation } from 'react-i18next'

import {
  DataResultTemplate,
  RemarkReport,
  TempLocationProps,
} from '@/@types/global'
import { Bending } from '@/@types/report/bending'
import { Chemical } from '@/@types/report/chemical'
import { CopperLayer } from '@/@types/report/copperLayer'
import { Ctod } from '@/@types/report/ctod'
import { Flaring } from '@/@types/report/flaring'
import { Flattening } from '@/@types/report/flattening'
import { FourPoint } from '@/@types/report/fourPoint'
import { Hardness } from '@/@types/report/hardness'
import { Hic } from '@/@types/report/hic'
import { Impact } from '@/@types/report/impact'
import { MicroCleanlines } from '@/@types/report/microCleanlines'
import { MicroHardness } from '@/@types/report/microHardness'
import { MicroStructure } from '@/@types/report/microStructure'
import { MicroStructurePhotos } from '@/@types/report/microStructurePhotos'
import { NaceA } from '@/@types/report/naceA'
import { NaceD } from '@/@types/report/naceD'
import { ReportItems } from '@/@types/report/report-items'
import { UserTypeProps } from '@/@types/userType'
import { http } from '@/api/http'
import '@/i18n'

type InitialProps = {
  name: string
  version: string
}

interface AppContextInterface {
  name: string
  version: string
  appTitle: string
  pageTitle?: string | null
  pageSubTitle?: string | null
  pageActions?: ReactNode | null
  isHeaderVisible: boolean
  isLoading: boolean
  setAppTitle: (value: string) => void
  setPageTitle: (value: string | null) => void
  setPageSubTitle: (value: string | null) => void
  setPageActions: (value: React.ReactNode | null) => void
  setHeaderVisibility: (value: boolean) => void
  setIsLoading: (value: boolean) => void
  user: UserTypeProps | undefined
  setUser: (value: UserTypeProps) => void
  tempLocation: TempLocationProps
  setTempLocation: (
    value: TempLocationProps | ((prev: TempLocationProps) => TempLocationProps)
  ) => void
  startDateApprovalFlow: Date | undefined
  setStartDateApprovalFlow: (startDate: Date | undefined) => void
  endDateApprovalFlow: Date | undefined
  setEndDateApprovalFlow: (endDate: Date | undefined) => void
  processOrderItemsApprovalFlow: string[] | undefined
  setProcessOrderItemsApprovalFlow: (value: string[] | undefined) => void
  pqLotItemsApprovalFlow: string[] | undefined
  setPqLotItemsApprovalFlow: (value: string[] | undefined) => void
  templateColumn: DataResultTemplate[]
  setTemplateColumn: (value: DataResultTemplate[]) => void
  templateRow: DataResultTemplate[]
  setTemplateRow: (value: DataResultTemplate[]) => void
  resultItems: ReportItems[]
  setResultItems: (value: ReportItems[]) => void
  remark: RemarkReport[] | null
  setRemark: (value: RemarkReport[]) => void
  executeSearch: boolean
  setExecuteSearch: (value: boolean) => void
  docCode: string
  setDocCode: (value: string) => void
}

interface AppProviderProps {
  children: ReactNode
}

const initialState: InitialProps = {
  name: import.meta.env.VITE_REACT_APP_NAME,
  version: import.meta.env.VITE_REACT_APP_VERSION,
}

const AppContext = React.createContext<AppContextInterface>(
  {} as AppContextInterface
)

function AppProvider({ children }: AppProviderProps) {
  const { t } = useTranslation()
  const [executeSearch, setExecuteSearch] = useState<boolean>(false)
  const [appTitle, setAppTitle] = useState<string>(t<string>('app.title'))
  const [pageTitle, setPageTitle] = useState<string | null>(null)
  const [pageActions, setPageActions] = useState<React.ReactNode | null>(null)
  const [pageSubTitle, setPageSubTitle] = useState<string | null>(null)
  const [isHeaderVisible, setHeaderVisibility] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [user, setUser] = useState<UserTypeProps>()
  const [tempLocation, setTempLocation] = useState<TempLocationProps>({
    path: '/',
  })
  const [startDateApprovalFlow, setStartDateApprovalFlow] = React.useState<
    Date | undefined
  >(undefined)
  const [endDateApprovalFlow, setEndDateApprovalFlow] = React.useState<
    Date | undefined
  >(undefined)
  const [processOrderItemsApprovalFlow, setProcessOrderItemsApprovalFlow] =
    React.useState<string[] | undefined>(undefined)
  const [pqLotItemsApprovalFlow, setPqLotItemsApprovalFlow] =
    React.useState<string[] | undefined>(undefined)
  const [templateColumn, setTemplateColumn] = useState<DataResultTemplate[]>([])
  const [templateRow, setTemplateRow] = useState<DataResultTemplate[]>([])
  const [resultItems, setResultItems] = useState<ReportItems[]>([])
  const [remark, setRemark] = useState<RemarkReport[] | null>(null)
  const [docCode, setDocCode] = useState<string>('')

  useEffect(() => {
    setAppTitle(t<string>('app.title'))
  }, [t])

  const cognito = {
    domain: import.meta.env.VITE_REACT_APP_SSO_COGNITO_DOMAIN,
    userPoolClientId: import.meta.env
      .VITE_REACT_APP_SSO_COGNITO_USER_POOL_CLIENT_ID,
    userPoolId: import.meta.env.VITE_REACT_APP_SSO_COGNITO_USER_POOL_ID,
  }
  const redirectUrl = import.meta.env.VITE_REACT_APP_URL

  return (
    <ThemeProvider theme={mixedTheme}>
      <GlobalStyle />
      <AuthSSOProvider
        domain={cognito.domain}
        userPoolClientId={cognito.userPoolClientId}
        userPoolId={cognito.userPoolId}
        redirectSignIn={[redirectUrl]}
        redirectSignOut={[redirectUrl]}
        http={http}
      >
        <ToastProvider>
          <AppContext.Provider
            value={{
              name: initialState.name,
              version: initialState.version,
              pageTitle,
              pageActions,
              appTitle,
              pageSubTitle,
              isHeaderVisible,
              isLoading,
              setAppTitle,
              setPageTitle,
              setPageSubTitle,
              setHeaderVisibility,
              setPageActions,
              setIsLoading,
              user,
              setUser,
              tempLocation,
              setTempLocation,
              templateColumn,
              setTemplateColumn,
              templateRow,
              setTemplateRow,
              resultItems,
              setResultItems,
              remark,
              setRemark,
              executeSearch,
              setExecuteSearch,
              docCode,
              setDocCode,
              startDateApprovalFlow,
              setStartDateApprovalFlow,
              endDateApprovalFlow,
              setEndDateApprovalFlow,
              processOrderItemsApprovalFlow,
              setProcessOrderItemsApprovalFlow,
              pqLotItemsApprovalFlow,
              setPqLotItemsApprovalFlow,
            }}
          >
            <BurgerMenuProvider>{children}</BurgerMenuProvider>
          </AppContext.Provider>
        </ToastProvider>
      </AuthSSOProvider>
    </ThemeProvider>
  )
}

function useApp(): AppContextInterface {
  return useContext(AppContext)
}

export { AppProvider, AppContext, useApp }
